import { http } from 'config';
import { User, type ProfileDeferredUserGroup, type ProfileSpotUserGroup } from 'types/swagger';

import {
  DEFERRED_USER_GROUPS_BY_ID_URL,
  DEFERRED_USER_GROUPS_URL,
  EMAIL_VALIDATION_RESEND_URL,
  PASSWORD_RESET_CONFIRM_URL,
  PASSWORD_RESET_URL,
  PASSWORD_RESET_VALIDATE_TOKEN_URL,
  PROFILE_BY_USER_ID_URL,
  SPOT_USER_GROUPS_BY_ID_URL,
  SPOT_USER_GROUPS_URL,
  TOKEN_LOGOUT_URL,
  TOKEN_ME_URL,
  TOKEN_MICROSOFT_PAIR_URL,
  TOKEN_PAIR_URL,
  USER_BY_ID_URL,
  USER_URL,
} from '../endpoints';
import { type Profile as ClientProfile, type User as ClientUser } from './interfaces';

export const userSignIn = async (
  credentials: { username: string; password: string },
  abortController: AbortSignal
) =>
  await http.post(TOKEN_PAIR_URL, credentials, {
    signal: abortController,
  });

export const userSignOut = async (refresh: string) =>
  await http.post(TOKEN_LOGOUT_URL, { refresh });

export const microsoftSignIn = async (accessToken: string) =>
  await http.post(TOKEN_MICROSOFT_PAIR_URL, {
    ms_entra_token: accessToken,
  });

export const fetchUser = async () => await http.get<User>(TOKEN_ME_URL);

export const getUsers = async (pageSize = 999) =>
  await http.get<{ results: User[] }>(USER_URL, { params: { page_size: pageSize } });

export const postUser = async (user: ClientUser) => await http.post(USER_URL, user);

export const patchUser = async (userId: number, user: ClientUser) =>
  await http.patch(USER_BY_ID_URL(userId), user);

// ! Sin uso, evaluar si se necesita (dudoso btw)
export const deleteUser = async (userId: number) => await http.delete(USER_BY_ID_URL(userId));

export const updateProfile = async (userId: number, profile: ClientProfile) =>
  await http.patch<Pick<ClientUser, 'profile'>>(PROFILE_BY_USER_ID_URL(userId), profile);

export const sendResetPasswordRequest = async (email: string) => {
  http.defaults.headers.common.Authorization = undefined;
  return await http.post(PASSWORD_RESET_URL, { email });
};

export const confirmPassword = async (password: string, token: string) => {
  http.defaults.headers.common.Authorization = undefined;
  return await http.post(PASSWORD_RESET_CONFIRM_URL, {
    password,
    token,
  });
};

export const validateToken = async (token: string) => {
  http.defaults.headers.common.Authorization = undefined;
  return await http.post(PASSWORD_RESET_VALIDATE_TOKEN_URL, { token });
};

export const reSendEmailValidation = async (email: string) =>
  await http.get(EMAIL_VALIDATION_RESEND_URL(email));

// * Spot User Groups
export const postProfileSpotUserGroup = async (newGroup: Omit<ProfileSpotUserGroup, 'id'>) =>
  await http.post<ProfileSpotUserGroup>(SPOT_USER_GROUPS_URL, newGroup);

export const deleteProfileSpotUserGroup = async (groupId: number) =>
  await http.delete(SPOT_USER_GROUPS_BY_ID_URL(groupId));

// * Deferred User Groups
export const postProfileDeferredUserGroup = async (
  newGroup: Omit<ProfileDeferredUserGroup, 'id'>
) => await http.post<ProfileDeferredUserGroup>(DEFERRED_USER_GROUPS_URL, newGroup);

export const deleteProfileDeferredUserGroup = async (groupId: number) =>
  await http.delete(DEFERRED_USER_GROUPS_BY_ID_URL(groupId));

// * Profile Spot User Groups
export const getSpotProfileUserGroups = async (pageSize = 999) =>
  await http.get<{ results: ProfileSpotUserGroup[] }>(SPOT_USER_GROUPS_URL, {
    params: { page_size: pageSize },
  });

// * Profile Deferred User Groups
export const getDeferredProfileUserGroups = async (pageSize = 999) =>
  await http.get<{ results: ProfileDeferredUserGroup[] }>(DEFERRED_USER_GROUPS_URL, {
    params: { page_size: pageSize },
  });
